import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";
import { type ProgrammeStatus, ProgrammeStatusDot } from "./programme-status-dot";

export type BadgeProgress = "default" | "todo" | "done" | "blocked";

/**
 * Чип прогресса с обводкой (DS, figma 1:8705). Заливка — цвет на 16%, обводка —
 * тот же цвет в полную силу: это единственное место, где семантический цвет
 * получает контур, и он же отличает бейдж от нейтрального StatusChip.
 */
const TONE: Record<BadgeProgress, { face: string; dot: ProgrammeStatus }> = {
	default: { face: "bg-chip", dot: "participated" },
	todo: { face: "bg-warning-soft shadow-[inset_0_0_0_1px_var(--color-warning)]", dot: "preparing" },
	done: {
		face: "bg-brand-soft shadow-[inset_0_0_0_1px_var(--color-brand-hover)]",
		dot: "participating",
	},
	blocked: {
		face: "bg-danger-soft shadow-[inset_0_0_0_1px_var(--color-danger)]",
		dot: "blocked",
	},
};

export interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
	progress?: BadgeProgress;
	/** `s` — 10px (внутри плотных списков), `m` — 13px (рядом с заголовком). */
	size?: "s" | "m";
	icon?: ReactNode;
	showDot?: boolean;
	children?: ReactNode;
}

export function StatusBadge({
	progress = "default",
	size = "s",
	icon,
	showDot = true,
	className,
	children,
	...rest
}: StatusBadgeProps) {
	const tone = TONE[progress];
	return (
		<span
			className={cn(
				"inline-flex w-fit items-center gap-0.5 whitespace-nowrap rounded-chip py-[1.5px] pr-1.5 pl-0.5",
				"font-mono text-fg tracking-[var(--ls-tight)]",
				size === "m" ? "text-[13px] leading-[1.4]" : "text-[10px] leading-[1.4]",
				tone.face,
				className,
			)}
			{...rest}
		>
			{icon ?? (showDot ? <ProgrammeStatusDot status={tone.dot} box={12} /> : null)}
			{children}
		</span>
	);
}
